import { useBunja } from 'bunja/react';
import { useAtom } from 'jotai';
import { useTranslations } from 'next-intl';

import { useAbFlag } from '@flipster/features/src/shared/ab-test';
import { TradingPauseNotice } from '@flipster/features/src/shared/trading-pause/notice';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { setLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import Actions from '@web/app/[locale]/(web)/trade/_misc/order-form/spot/Actions';
import OrderAmountForm from '@web/app/[locale]/(web)/trade/_misc/order-form/spot/OrderAmountForm';
import OrderTypeInfo from '@web/app/[locale]/(web)/trade/_misc/order-form/spot/OrderTypeInfo';
import PriceForm from '@web/app/[locale]/(web)/trade/_misc/order-form/spot/PriceForm';
import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import TradingFee from './TradingFee';
import { spotFormBunja, type Side } from './bunja/form';

const Tabs = createTabsComponent<Side>();
const Choice = createSingleChoice<'market' | 'trigger' | 'limit'>();

const SpotOrderForm = () => {
  const isSpotLimitOrderEnabled = useAbFlag('25q4-spot-limit-order', 'on');
  const { sideAtom, priceTypeAtom } = useBunja(spotFormBunja);
  const { symbol } = useBunja(tradeSymbolBunja);

  const [side, setSide] = useAtom(sideAtom);
  const [priceType, setPriceType] = useAtom(priceTypeAtom);

  const transTrade = useTranslations('Trade.r250401');
  const transTradeLimit = useTranslations('Trade.r251104');

  const isMounted = useMounted();

  const onClickSideTab = (val: Side) => {
    setSide(val);
    setLocalStorage('flipster.configs.v1.spot.sideTypeTab', val);
    segment.track(TradeEvent.TabClick, { symbol, tab_type: val });
  };

  const onClickPriceTab = (val: 'market' | 'trigger' | 'limit') => {
    setPriceType(val);
    segment.track(TradeEvent.TabClick, { symbol, tab_type: val });
  };

  if (!isMounted) return null;
  return (
    <div>
      <Tabs type='fixed' border value={side} onChange={onClickSideTab}>
        <Tabs.Tab label={transTrade('Buy')} value='Buy' />
        <Tabs.Tab label={transTrade('Sell')} value='Sell' />
      </Tabs>
      <div
        className={css({
          // 62(symbolInfo) + 550(chart) + 10(gap) - 32.6(tab) - 16(tab margin)
          minHeight: 'calc(62px + 550px + 10px - 32.6px - 16px)',
          py: 20,
          px: 16,
        })}
      >
        <Choice type='square' value={priceType} onChange={onClickPriceTab}>
          {isSpotLimitOrderEnabled && <Choice.Chip label={transTradeLimit('Limit')} value='limit' />}
          <Choice.Chip value='market' label={transTrade('Tab_market')} />
          <Choice.Chip value='trigger' label={transTrade('Tab_trigger')} />
        </Choice>
        <TradingPauseNotice symbol={symbol} />
        <PriceForm />
        <OrderAmountForm />
        <Actions />
        <OrderTypeInfo />
        <TradingFee />
      </div>
    </div>
  );
};

export default SpotOrderForm;
